// ===== COMMON UTILITIES AND CONFIGURATIONS =====

// API Configuration
const API_CONFIG = {
  baseUrl: window.apiUrl, // Use current domain
  customerToken: null,
  productHandle: null,
};

// ===== FILE UPLOAD FUNCTIONALITY =====

// Current upload XHR reference for cancellation
let currentUploadXhr = null;

/**
 * Upload file to server
 * Handles both anonymous and authenticated users
 * @param {File} file - The file to upload
 * @param {string} productId - The product ID
 * @returns {Promise<Object>} The uploaded file response
 */
async function uploadFileToServer(file, productId) {
  // Show upload progress bar
  updateUploadProgress(0, "uploading");

  // Get customer access token (if user is authenticated)
  const customerAccessToken = JSON.parse(
    localStorage.getItem(window.customerAccessTokenKey)
  );
  const accessToken = customerAccessToken
    ? customerAccessToken.accessToken
    : null;

  // Get anonymous user ID (if user is anonymous)
  const anonymousUserData = JSON.parse(
    localStorage.getItem("user_anonymous")
  );
  const anonymousCustomerId = anonymousUserData
    ? anonymousUserData.uuid
    : null;

  // Create FormData for file upload
  const formData = new FormData();
  formData.append("file", file);
  formData.append("productId", productId);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    currentUploadXhr = xhr;
    xhr.open("POST", `${window.apiUrl}/api/upload`);

    // Set headers
    xhr.setRequestHeader("Accept", "application/json");
    if (accessToken) {
      xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
    } else if (anonymousCustomerId) {
      xhr.setRequestHeader("X-Customer-ID", anonymousCustomerId);
    }

    // Track upload progress
    xhr.upload.addEventListener("progress", function (e) {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        updateUploadProgress(percent, "uploading");
      }
    });

    xhr.addEventListener("load", function () {
      currentUploadXhr = null;
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText);
          if (!result.success || !result.data) {
            updateUploadProgress(0, "error");
            reject(new Error("Upload response missing data"));
            return;
          }
          updateUploadProgress(100, "completed");
          resolve(result);
        } catch (e) {
          updateUploadProgress(0, "error");
          reject(new Error("Invalid response from server"));
        }
      } else {
        updateUploadProgress(0, "error");
        reject(
          createHttpError(
            `Upload failed: ${xhr.status} ${xhr.statusText}`,
            xhr.status
          )
        );
      }
    });

    xhr.addEventListener("error", function () {
      currentUploadXhr = null;
      updateUploadProgress(0, "error");
      reject(createHttpError("File upload failed: network error", 0));
    });

    xhr.addEventListener("abort", function () {
      currentUploadXhr = null;
      reject(new Error("Upload cancelled"));
    });

    xhr.send(formData);
  });
}

/**
 * Update upload progress bar UI
 * @param {number} percent - Progress percentage (0-100)
 * @param {string} state - "uploading", "completed", or "error"
 */
function updateUploadProgress(percent, state) {
  const progressBar = document.getElementById("uploadProgressBar");
  const progressFill = document.getElementById("uploadProgressFill");
  const statusText = document.getElementById("uploadStatusText");

  if (!progressBar || !progressFill || !statusText) return;

  if (state === "uploading") {
    progressBar.style.display = "block";
    statusText.style.display = "block";
    progressFill.style.width = percent + "%";
    progressFill.classList.remove("completed");
    statusText.classList.remove("completed");
    statusText.textContent = "アップロード中... " + percent + "%";
  } else if (state === "completed") {
    progressFill.style.width = "100%";
    progressFill.classList.add("completed");
    statusText.classList.add("completed");
    statusText.textContent = "アップロード完了";
  } else if (state === "error") {
    progressBar.style.display = "none";
    statusText.style.display = "block";
    statusText.classList.remove("completed");
    statusText.style.color = "#e53935";
    statusText.textContent = "アップロード失敗";
  }
}

/**
 * Cancel the current file upload if in progress
 */
function cancelFileUpload() {
  if (currentUploadXhr) {
    currentUploadXhr.abort();
    currentUploadXhr = null;
  }
}

/**
 * Show upload progress UI
 * @param {File} file - The file being uploaded
 */
function showUploadProgress(file) {
  const uploadFrame = document.getElementById("uploadFrame");
  const fileUploadProgress = document.getElementById("fileUploadProgress");
  const fileName = document.getElementById("fileName");
  const fileSize = document.getElementById("fileSize");
  const fileStatus = document.getElementById("fileStatus");

  if (uploadFrame && fileUploadProgress && fileName && fileSize && fileStatus) {
    // Show upload progress
    fileUploadProgress.style.display = "flex";

    // Update file info
    fileName.textContent = file.name;
    fileSize.textContent = `0 KB of ${formatFileSize(file.size)} •`;
    fileStatus.innerHTML = '<span class="status-text">アップロード中...</span>';
  }
}

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
function formatFileSize(bytes) {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

// ===== TOAST NOTIFICATION SYSTEM =====
class ToastNotification {
  constructor() {
    this.createToastContainer();
  }

  createToastContainer() {
    if (!document.getElementById("toast-container")) {
      const container = document.createElement("div");
      container.id = "toast-container";
      container.style.cssText = `
                position: fixed;
                top: 20px;
                right: 20px;
                z-index: 9999;
                display: flex;
                flex-direction: column;
                gap: 10px;
                max-width: 400px;
            `;
      document.body.appendChild(container);
    }
  }

  show(message, type = "info", duration = 5000) {
    const container = document.getElementById("toast-container");
    if (!container) return;

    const toast = document.createElement("div");
    toast.className = `toast-notification toast-${type}`;
    toast.style.cssText = `
            background: white;
            border: 2px solid ${this.getBorderColor(type)};
            border-radius: 12px;
            padding: 16px 20px;
            box-shadow: 0 8px 32px rgba(239, 98, 61, 0.15);
            backdrop-filter: blur(10px);
            transform: translateX(100%);
            opacity: 0;
            transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
            display: flex;
            align-items: center;
            gap: 12px;
            min-width: 300px;
            max-width: 400px;
            position: relative;
            overflow: hidden;
        `;

    // Add gradient background for success
    if (type === "success") {
      toast.style.background = "linear-gradient(135deg, #fff 0%, #fff5f2 100%)";
    }

    // Icon
    const icon = document.createElement("div");
    icon.className = "toast-icon";
    icon.innerHTML = this.getIcon(type);
    icon.style.cssText = `
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: ${this.getIconBackground(type)};
            color: ${this.getIconColor(type)};
            flex-shrink: 0;
        `;

    // Content
    const content = document.createElement("div");
    content.className = "toast-content";
    content.style.cssText = `
            flex: 1;
            color: #333;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 14px;
            line-height: 1.4;
            font-weight: 500;
        `;
    content.textContent = message;

    // Close button
    const closeBtn = document.createElement("button");
    closeBtn.className = "toast-close";
    closeBtn.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/>
            </svg>
        `;
    closeBtn.style.cssText = `
            background: none;
            border: none;
            color: #999;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: all 0.2s ease;
            flex-shrink: 0;
        `;

    // Progress bar
    const progressBar = document.createElement("div");
    progressBar.className = "toast-progress";
    progressBar.style.cssText = `
            position: absolute;
            bottom: 0;
            left: 0;
            height: 3px;
            background: ${this.getBorderColor(type)};
            width: 100%;
            transform: scaleX(1);
            transform-origin: left;
            transition: transform ${duration}ms linear;
        `;

    // Assemble toast
    toast.appendChild(icon);
    toast.appendChild(content);
    toast.appendChild(closeBtn);
    toast.appendChild(progressBar);

    // Add to container
    container.appendChild(toast);

    // Animate in
    setTimeout(() => {
      toast.style.transform = "translateX(0)";
      toast.style.opacity = "1";
    }, 10);

    // Start progress bar
    setTimeout(() => {
      progressBar.style.transform = "scaleX(0)";
    }, 100);

    // Auto remove
    const removeToast = () => {
      toast.style.transform = "translateX(100%)";
      toast.style.opacity = "0";
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    };

    // Event listeners
    closeBtn.addEventListener("click", removeToast);
    closeBtn.addEventListener("mouseenter", () => {
      closeBtn.style.color = "#EF623D";
      closeBtn.style.background = "#fff5f2";
    });
    closeBtn.addEventListener("mouseleave", () => {
      closeBtn.style.color = "#999";
      closeBtn.style.background = "none";
    });

    // Auto remove after duration
    setTimeout(removeToast, duration);

    return toast;
  }

  getBorderColor(type) {
    switch (type) {
      case "success":
        return "#198754"; // Bootstrap success green
      case "error":
        return "#dc3545"; // Bootstrap danger red
      case "warning":
        return "#ffc107"; // Bootstrap warning yellow
      case "info":
        return "#0d6efd"; // Bootstrap primary blue
      default:
        return "#198754";
    }
  }

  getIconBackground(type) {
    switch (type) {
      case "success":
        return "#198754"; // Bootstrap success green
      case "error":
        return "#dc3545"; // Bootstrap danger red
      case "warning":
        return "#ffc107"; // Bootstrap warning yellow
      case "info":
        return "#0d6efd"; // Bootstrap primary blue
      default:
        return "#198754";
    }
  }

  getIconColor(type) {
    return "white";
  }

  getIcon(type) {
    switch (type) {
      case "success":
        return `
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                    </svg>
                `;
      case "error":
        return `
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                    </svg>
                `;
      case "warning":
        return `
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                    </svg>
                `;
      case "info":
        return `
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                    </svg>
                `;
      default:
        return `
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                    </svg>
                `;
    }
  }

  success(message, duration = 4000) {
    return this.show(message, "success", duration);
  }

  error(message, duration = 5000) {
    return this.show(message, "error", duration);
  }

  warning(message, duration = 4000) {
    return this.show(message, "warning", duration);
  }

  info(message, duration = 4000) {
    return this.show(message, "info", duration);
  }
}

// Initialize toast notification system globally
let toast = null;

// Initialize toast when DOM is ready
function initializeToast() {
  if (!toast && document.body) {
    toast = new ToastNotification();
  }
}

// Initialize toast immediately if DOM is already ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeToast);
} else {
  initializeToast();
}

// Toast helper function to ensure toast is available
function showToast(message, type = "info", duration = 5000) {
  if (!toast) {
    initializeToast();
  }

  if (toast && typeof toast.show === "function") {
    toast.show(message, type, duration);
  } else {
    // Fallback: show alert for errors
    if (type === "error") {
      alert(`Error: ${message}`);
    }
  }
}

// ===== CUSTOMER-FACING ERROR MESSAGES =====

const CONTACT_PAGE_PATH = "/pages/contact";

/**
 * HTTP ステータス付きの Error を作る（0 は通信断）。
 * message は開発者向けのまま残し、画面に出す文言は toUserErrorMessage で決める。
 * @param {string} message - 開発者向けのメッセージ
 * @param {number} status - HTTP ステータス
 * @returns {Error}
 */
function createHttpError(message, status) {
  const error = new Error(message);
  error.status = status;
  return error;
}

/**
 * そのまま画面に出してよい日本語の文言を持つ Error を作る（入力不足などの検証エラー用）。
 * @param {string} message - お客さま向けの文言
 * @returns {Error}
 */
function createUserError(message) {
  const error = new Error(message);
  error.userMessage = message;
  return error;
}

/**
 * 例外をお客さま向けの日本語の文言に変える。
 * サーバーやブラウザの英語のメッセージは画面に出さず、原因（HTTP ステータス・通信断）で文言を選ぶ。
 * @param {Error} error - 発生した例外
 * @param {string} fallbackMessage - 原因が分からないときに出す「何が失敗したか」（例: "デザインの保存に失敗しました。"）
 * @returns {string}
 */
function toUserErrorMessage(error, fallbackMessage) {
  if (error && error.userMessage) {
    return error.userMessage;
  }

  const status = error && typeof error.status === "number" ? error.status : null;
  const isNetworkError =
    status === 0 ||
    (error instanceof TypeError &&
      /fetch|network|load failed/i.test(error.message || ""));

  if (status === 401 || status === 403) {
    return "ログインの有効期限が切れた可能性があります。ページを再読み込みし、ログインし直してからもう一度お試しください。";
  }
  if (status === 413) {
    return "ファイルのサイズが大きすぎます。サイズを小さくしてからもう一度お試しください。";
  }
  if (isNetworkError) {
    return "通信に失敗しました。通信環境をご確認のうえ、もう一度お試しください。";
  }
  return `${fallbackMessage}時間をおいてもう一度お試しください。解決しない場合はお問い合わせください。`;
}

// ===== ログインの確認（アクセストークン）が切れたとき =====

// ログイン画面へ移る前に、案内のトーストを読める時間
const RE_LOGIN_REDIRECT_DELAY_MS = 1500;
let isRedirectingToReLogin = false;
// 開いた時点でログイン画面へ移したページか（初期化を途中でやめているので、そのままでは使えない）
let hasStoppedPageForReLogin = false;

/**
 * API の 401 は「ログインの確認が切れた（トークンが無い・使えない）」とみなす。
 * 403 は権限の問題なので、ログインし直しても直らない。
 * @param {Error} error
 * @returns {boolean}
 */
function isCustomerAuthError(error) {
  return Boolean(error && error.status === 401);
}

/**
 * アクセストークンの有効期限（expiresAt）が過ぎているか。
 * 期限切れのトークンはバックエンドで会員として扱われない（デザインを保存・表示できない）ので、無いものとして扱う。
 * expiresAt が無い・読めないときは、期限切れとはみなさない（送ってみて、401 ならログインし直してもらう）
 * @param {{ expiresAt?: string }} customerAccessToken
 * @returns {boolean}
 */
function isCustomerAccessTokenExpired(customerAccessToken) {
  const expiresAt = Date.parse(customerAccessToken && customerAccessToken.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt <= Date.now();
}

// デザインの保存などに使うアクセストークンを読む（無い・壊れている・有効期限が過ぎているときは null）。
// シミュレーター系のレイアウトには window.customerAccessTokenKey が無いので、同じキー名を既定にする
function readStoredCustomerAccessToken() {
  try {
    const customerAccessToken = JSON.parse(
      localStorage.getItem(window.customerAccessTokenKey || "customerAccessToken")
    );
    return customerAccessToken &&
      customerAccessToken.accessToken &&
      !isCustomerAccessTokenExpired(customerAccessToken)
      ? customerAccessToken
      : null;
  } catch (_) {
    return null;
  }
}

function clearStoredCustomerAccessToken() {
  try {
    localStorage.removeItem(window.customerAccessTokenKey || "customerAccessToken");
  } catch (_) {
    // 保存できない環境では何も残っていない
  }
}

// 今のページ（path + search）を戻り先にした、ログイン画面の URL
function buildReLoginUrl() {
  const loginUrl = new URL("/account/login", window.location.origin);
  loginUrl.searchParams.set(
    "return_to",
    window.location.pathname + window.location.search
  );
  return loginUrl.pathname + loginUrl.search;
}

/**
 * 使えなくなったアクセストークンを消して、ログイン画面へ案内する。ログインし直したら今のページへ戻る。
 * ログイン画面は、Shopify にログイン済みでもトークンが無ければフォームを出す（script.login.js）。
 * @param {string} [message] - 移る前にトーストで出す案内
 * @param {{ replace?: boolean }} [options] - replace: 開いた時点で移すとき（初期化を途中でやめるページ）。
 *   履歴を置き換えて、ログイン画面で「戻る」を押すと、その前のページ（商品ページなど）へ戻れるようにする
 */
function redirectToReLogin(message, options) {
  if (isRedirectingToReLogin) return;
  isRedirectingToReLogin = true;

  const shouldReplace = Boolean(options && options.replace);
  if (shouldReplace) {
    hasStoppedPageForReLogin = true;
  }

  clearStoredCustomerAccessToken();
  if (message) {
    showToast(message, "info");
  }

  const loginUrl = buildReLoginUrl();
  setTimeout(
    function () {
      // 移る前に別のページへ移り、「戻る」で bfcache から戻されて取りやめになっていたら移らない
      if (!isRedirectingToReLogin) return;
      if (shouldReplace) {
        window.location.replace(loginUrl);
      } else {
        window.location.href = loginUrl;
      }
    },
    message ? RE_LOGIN_REDIRECT_DELAY_MS : 0
  );
}

// ログイン画面へ移った後に、ブラウザの「戻る」で bfcache から復元されたとき
// - 開いた時点で移したページは初期化を途中でやめているので、読み込み直す（ログインし直していれば、そのまま使える）
// - 作業の途中で移したページは、続けて使えるように案内中の状態を戻す（保存や注文のときに、もう一度ログイン画面へ案内する）
window.addEventListener("pageshow", function (event) {
  if (!event.persisted) return;
  if (hasStoppedPageForReLogin) {
    window.location.reload();
    return;
  }
  isRedirectingToReLogin = false;
});

// バックエンドが画像の保存に使う Google Cloud Storage のホスト。https の画像はこのホストのものだけを使う
const DESIGN_IMAGE_HTTPS_HOST = "storage.googleapis.com";
// バックエンドは、POST で作ったデザインの画像（中身は PNG）を data:application/octet-stream;base64,… で返す。
// PUT で保存し直したものは data:image/png;base64,… になる。どちらも img・ダウンロードで使えるので受け付ける
const DESIGN_IMAGE_DATA_URL_PREFIXES = [
  "data:image/",
  "data:application/octet-stream;base64,",
];

/**
 * API から来た画像の URL のうち、デザインの画像として使ってよいものだけを返す。
 * - data:image/… と data:application/octet-stream;base64,…（バックエンドが実際に返す形）
 * - https://storage.googleapis.com/…（バックエンドの画像の保存先）
 * javascript: などの URL や、外部のホストの画像（開いただけで外部へ通信が出る）を img に入れないため。
 * @param {*} value
 * @returns {string|null}
 */
function getSafeImageSrc(value) {
  if (typeof value !== "string") return null;
  if (DESIGN_IMAGE_DATA_URL_PREFIXES.some((prefix) => value.startsWith(prefix))) {
    return value;
  }

  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname === DESIGN_IMAGE_HTTPS_HOST
      ? url.href
      : null;
  } catch (_) {
    return null;
  }
}

// ===== 会員登録の一時情報の消去 =====
// 会員登録の1段目で一時的に残したログイン情報（script.register.js の userInfoHash）は、2段目でしか使わない。
// 全ページの消去は script.return-to.js が行うが、シミュレーター系のページ（no-header-footer レイアウト）はそれを読み込まないので、ここでも消す。
// 消し方は script.return-to.js の clearRegistrationUserInfoHash と同じ（会員登録ページと、期限内の /challenge では残す）
(function clearRegistrationUserInfoHashOnThisPage() {
  const userInfoHashKey = "userInfoHash";
  const userInfoHashTtlMs = 10 * 60 * 1000;

  try {
    localStorage.removeItem(userInfoHashKey);
  } catch (_) {
    // 保存できない環境では何も残っていない
  }

  const path = window.location.pathname;
  if (/\/account\/register\/?$/.test(path)) return;
  if (/\/challenge\/?$/.test(path) && !isUserInfoHashExpired()) return;

  try {
    sessionStorage.removeItem(userInfoHashKey);
  } catch (_) {
    // 保存できない環境では何も残っていない
  }

  // 無い・壊れている・期限切れのときは true
  function isUserInfoHashExpired() {
    try {
      const stash = JSON.parse(sessionStorage.getItem(userInfoHashKey));
      return (
        !stash ||
        typeof stash.savedAt !== "number" ||
        Date.now() - stash.savedAt > userInfoHashTtlMs
      );
    } catch (_) {
      return true;
    }
  }
})();

// ===== UTILITY FUNCTIONS =====

// Generate UUID for anonymous users
function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Initialize anonymous user if not logged in
function initializeAnonymousUser() {
  const customerToken = localStorage.getItem("customerAccessToken");

  if (!customerToken) {
    let userAnonymous = localStorage.getItem("user_anonymous");

    if (!userAnonymous) {
      const anonymousUser = {
        uuid: generateUUID(),
      };

      localStorage.setItem("user_anonymous", JSON.stringify(anonymousUser));
    } else {
    }
  } else {
  }
}

// Initialize anonymous user on page load
document.addEventListener("DOMContentLoaded", function () {
  initializeAnonymousUser();
});

// Get current user (logged in or anonymous) - enhanced with window.isLoggedIn
function getCurrentUser() {
  // First check if user is logged in via Liquid-provided window.isLoggedIn
  if (window.isLoggedIn === true) {
    // 無い・壊れている・有効期限が過ぎているトークンは使わない（呼び出し元でログインし直してもらう）
    const customerToken = readStoredCustomerAccessToken();
    if (customerToken) {
      return {
        type: "authenticated",
        token: customerToken.accessToken,
      };
    }
  } else if (window.isLoggedIn === false) {
    // Check for anonymous user data
    const userAnonymous = localStorage.getItem("user_anonymous");
    if (userAnonymous) {
      try {
        return {
          type: "anonymous",
          ...JSON.parse(userAnonymous),
        };
      } catch (error) {
        // Error parsing anonymous user
      }
    }
    return null;
  } else {
    // Fallback to original logic if window.isLoggedIn is not set
    const customerToken = localStorage.getItem("customerAccessToken");

    if (customerToken) {
      return {
        type: "authenticated",
        token: customerToken,
      };
    } else {
      const userAnonymous = localStorage.getItem("user_anonymous");
      if (userAnonymous) {
        try {
          return {
            type: "anonymous",
            ...JSON.parse(userAnonymous),
          };
        } catch (error) {
          return null;
        }
      }
      return null;
    }
  }

  return null;
}

// Get customer access token
function getCustomerToken() {
  return localStorage.getItem("customerAccessToken");
}

// Extract numeric product ID from Shopify GID
function extractProductHandle(productHandle) {
  if (!productHandle) return null;

  if (
    typeof productHandle === "string" &&
    productHandle.startsWith("gid://shopify/Product/")
  ) {
    const numericId = productHandle.split("/").pop();
    return numericId;
  }

  return productHandle.toString();
}

// Initialize API configuration
// Note: Product Handle iphone-16 is used for the badge simulator
function initializeApiConfig() {
  if (
    !API_CONFIG.customerToken &&
    typeof window.Shopify !== "undefined" &&
    window.Shopify.customerAccessToken
  ) {
    API_CONFIG.customerToken = window.Shopify.customerAccessToken;
  }

  if (!API_CONFIG.productHandle) {
    const urlParams = new URLSearchParams(window.location.search);
    const rawProductHandle =
      urlParams.get("product_handle") || window.productHandle;

    if (rawProductHandle) {
      API_CONFIG.productHandle = extractProductHandle(rawProductHandle);
    } else {
      API_CONFIG.productHandle = null;
    }
  }
}

// Make API request with authentication
async function makeApiRequestWithCurrentUser(endpoint, options = {}) {
  const currentUser = getCurrentUser();

  // Shopify にはログインしているのにアクセストークンが無いときは、送っても受け付けられないので 401 として扱う
  if (!currentUser && window.isLoggedIn === true) {
    throw createHttpError("Customer access token is missing", 401);
  }

  // Determine if this is an anonymous API call
  const isAnonymousCall = currentUser && currentUser.type === "anonymous";
  const apiPrefix = isAnonymousCall ? "/anonymous" : "";
  const fullEndpoint = `${apiPrefix}${endpoint}`;

  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (currentUser && currentUser.type === "authenticated") {
    headers["Authorization"] = `Bearer ${currentUser.token}`;
  } else if (currentUser && currentUser.type === "anonymous") {
    headers["X-Customer-ID"] = currentUser.uuid;
  }

  try {
    const response = await fetch(`${API_CONFIG.baseUrl}/api${fullEndpoint}`, {
      method: options.method || "GET",
      headers: headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw createHttpError(
        `HTTP ${response.status}: ${response.statusText}`,
        response.status
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
}

// Convert data URL to blob
function dataURLToBlob(dataURL) {
  const arr = dataURL.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

// Extract canvas data from design response
function extractCanvasData(design) {
  let canvasData = null;
  let canvasObjects = null;
  if (design.metadata) {
    if (design.metadata.canvas_data) {
      canvasData = design.metadata.canvas_data;
    } else if (design.metadata.canvasData) {
      // 一時保存（script.simulator.js の saveDesignToAPI）は canvasData の名前で保存している
      canvasData = design.metadata.canvasData;
    }
    if (design.metadata.canvas_objects) {
      canvasObjects = design.metadata.canvas_objects;
    } else if (design.metadata.canvasObjects) {
      canvasObjects = design.metadata.canvasObjects;
    }
  }

  if (!canvasData && design.canvas_data) {
    canvasData = design.canvas_data;
  }
  
  if (!canvasObjects && design.canvas_objects) {
    canvasObjects = design.canvas_objects;
  }

  return {
    canvasData: canvasData,
    canvasObjects: canvasObjects
  };
}

// Show product required modal
function showProductRequiredModal() {
  const currentLang = document.documentElement.lang || "en";
  const isJapanese = currentLang === "ja" || currentLang === "ja-JP";

  const translations = isJapanese
    ? {
        title: "⚠️ 商品が選ばれていません",
        message1: "デザインの作成は、商品ページから始めてください。",
        message2:
          "商品ページの価格表で数量と納期を選び、「注文する」からお進みください。",
        goBackButton: "前のページに戻る",
      }
    : {
        title: "⚠️ Product Handle Required",
        message1: "To view this page, a product Handle is required.",
        message2:
          "Require <code>?product_handle={PRODUCT_HANDLE}</code> in the URL.",
        goBackButton: "Go Back",
      };

  const modalHTML = `
        <div id="product-required-modal" class="modal-overlay">
            <div class="modal-content">
                <div class="modal-header">
                    <h3>${translations.title}</h3>
                </div>
                <div class="modal-body">
                    <p>${translations.message1}</p>
                    <p>${translations.message2}</p>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-secondary" onclick="goBack()">${translations.goBackButton}</button>
                </div>
            </div>
        </div>
    `;

  document.body.insertAdjacentHTML("beforeend", modalHTML);

  setTimeout(() => {
    const modal = document.getElementById("product-required-modal");
    if (modal) {
      modal.classList.add("show");
    }
  }, 100);
}

// Close modal and go back
function closeModal() {
  const modal = document.getElementById("product-required-modal");
  if (modal) {
    modal.classList.remove("show");
    setTimeout(() => {
      modal.remove();
    }, 300);
  }
}

// Show delete confirmation modal
function showDeleteConfirmationModal(designId) {
  // Get current language
  const currentLang = document.documentElement.lang || "en";
  const isJapanese = currentLang === "ja" || currentLang === "ja-JP";

  // Get translations
  const translations = isJapanese
    ? {
        title: "デザインを削除",
        message: "このデザインを削除しますか？この操作は元に戻せません。",
        cancelButton: "キャンセル",
        deleteButton: "削除する",
      }
    : {
        title: "Delete Design",
        message:
          "Are you sure you want to delete this design? This action cannot be undone.",
        cancelButton: "Cancel",
        deleteButton: "Delete",
      };

  // Create modal HTML（文言は固定のもの。デザイン ID は HTML に入れず、削除ボタンのイベントで渡す）
  const modalHTML = `
        <div id="delete-confirmation-modal" class="modal-overlay">
            <div class="modal-content delete-modal">
                <div class="modal-header">
                    <h3>${translations.title}</h3>
                </div>
                <div class="modal-body">
                    <p>${translations.message}</p>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" onclick="closeDeleteModal()">${translations.cancelButton}</button>
                    <button type="button" class="btn btn-danger">${translations.deleteButton}</button>
                </div>
            </div>
        </div>
    `;

  // Add modal to body
  document.body.insertAdjacentHTML("beforeend", modalHTML);
  const deleteButton = document.body.lastElementChild.querySelector(".btn-danger");
  if (deleteButton) {
    deleteButton.addEventListener("click", function () {
      confirmDeleteDesign(designId);
    });
  }

  // Show modal with animation
  setTimeout(() => {
    const modal = document.getElementById("delete-confirmation-modal");
    if (modal) {
      modal.classList.add("show");
    }
  }, 100);
}

// Handle design item actions
function handleDesignAction(action, designId) {
  switch (action) {
    case "download":
      downloadDesign(designId);
      break;
    case "edit":
      loadDesignFromAPI(designId);
      break;
    case "delete":
      showDeleteConfirmationModal(designId);
      break;
  }
}

// Add event listeners for design action buttons
document.addEventListener("DOMContentLoaded", function () {
  // Handle download and edit buttons
  const actionButtons = document.querySelectorAll(".action-btn");
  actionButtons.forEach((button) => {
    button.addEventListener("click", function (e) {
      e.stopPropagation();
      const action = this.classList.contains("download-btn")
        ? "download"
        : "edit";
      const designItem = this.closest(".design-item");
      const designId = designItem
        ? designItem.dataset.designId || "design-" + Date.now()
        : "unknown";
      handleDesignAction(action, designId);
    });
  });

  // Handle delete text buttons
  const deleteButtons = document.querySelectorAll(".delete-text-btn");
  deleteButtons.forEach((button) => {
    button.addEventListener("click", function (e) {
      e.stopPropagation();
      const designItem = this.closest(".design-item");
      const designId = designItem
        ? designItem.dataset.designId || "design-" + Date.now()
        : "unknown";
      handleDesignAction("delete", designId);
    });
  });
});

// Go back to previous page
function goBack() {
  closeModal();
  setTimeout(() => {
    window.history.back();
  }, 300);
}

// Close delete confirmation modal
function closeDeleteModal() {
  const modal = document.getElementById("delete-confirmation-modal");
  if (modal) {
    modal.classList.remove("show");
    setTimeout(() => {
      modal.remove();
    }, 300);
  }
}

// Confirm and execute design deletion
function confirmDeleteDesign(designId) {
  // Close modal first
  closeDeleteModal();

  // Execute deletion
  deleteDesignFromAPI(designId);
}

// Check if current page is main simulator
function isMainSimulator() {
  return (
    window.location.pathname.includes("/simulator") &&
    !window.location.pathname.includes("trial-simulator") &&
    !window.location.pathname.includes("overview-simulator")
  );
}

// Get current language
function getCurrentLanguage() {
  const currentLang = document.documentElement.lang || "en";
  return currentLang === "ja" || currentLang === "ja-JP" ? "ja" : "en";
}

// ===== MIGRATE ANONYMOUS DESIGNS API =====
// Migrate anonymous designs to authenticated user account
async function migrateAnonymousDesigns(accessToken) {
  try {
    // Check if there's anonymous user data to migrate
    const userAnonymous = localStorage.getItem("user_anonymous");
    if (!userAnonymous) {
      return { success: true, message: "移行する匿名デザインがありません" };
    }

    let anonymousUserData;
    try {
      anonymousUserData = JSON.parse(userAnonymous);
    } catch (error) {
      return { success: false, message: "無効な匿名ユーザーデータです" };
    }

    if (!anonymousUserData.uuid) {
      return { success: true, message: "移行する匿名デザインがありません" };
    }

    // Call API to migrate designs
    const response = await fetch(
      `${API_CONFIG.baseUrl}/api/migrate-anonymous-designs`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          anonymous_customer_id: anonymousUserData.uuid,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMessage =
        errorData?.error || `Migration failed with status: ${response.status}`;
      return { success: false, message: errorMessage };
    }

    const result = await response.json();

    // Clear anonymous user data after successful migration
    localStorage.removeItem("user_anonymous");

    // Show success message
    const currentLang = document.documentElement.lang || "en";
    const isJapanese = currentLang === "ja" || currentLang === "ja-JP";
    const successMessage = isJapanese
      ? `${result.migrated_count || 0}件のデザインがアカウントに移行されました`
      : `${result.migrated_count || 0} designs migrated to your account`;
    return {
      success: true,
      message: successMessage,
      data: result,
    };
  } catch (error) {
    const currentLang = document.documentElement.lang || "en";
    const isJapanese = currentLang === "ja" || currentLang === "ja-JP";
    const errorMessage = isJapanese
      ? "デザインの移行中にエラーが発生しました: " + error.message
      : "Error during design migration: " + error.message;

    // Show error message if available
    if (typeof showToast === "function") {
      showToast(errorMessage, "error");
    }
    return { success: false, message: error.message };
  }
}

// ===== CHECKOUT API INTEGRATION =====

/**
 * Add to cart using create-checkout API
 * @param {string} productHandle - Product handle (e.g., "iphone-16")
 * @param {number} quantity - Quantity to add
 * @param {string} variantId - Variant ID (optional)
 * @param {number} deliveryDays - Delivery days (optional)
 * @param {string} source - 計測用の流入元（商品ページは "product_page"、シミュレーター経由は "simulator"）
 * @param {number} unitPriceExcludingTax - 税抜単価（任意）。create-checkout の結果から単価が読めないときに order_complete 用に使う
 * @returns {Promise<Object>} API response
 * 失敗したときはトーストを出さずに例外を投げる。画面への表示は呼び出し元で1回だけ行う。
 */
async function addToCartWithCheckout(
  productHandle,
  quantity,
  variantId = null,
  deliveryDays = null,
  designId = null,
  productName = null,
  manuscriptMethod = null,
  source = "product_page",
  unitPriceExcludingTax = null
) {
  try {
    // Validate required parameters
    if (!productHandle || !quantity) {
      throw createUserError(
        "商品または数量の情報が見つかりません。商品ページで数量と納期を選び直してください。"
      );
    }
    if (!designId) {
      throw createUserError(
        "デザインの情報が見つかりません。お手数ですが、もう一度デザインをご用意ください。"
      );
    }

    // Prepare form data
    const formData = new FormData();
    formData.append("productHandle", productHandle);
    formData.append("quantity", quantity.toString());

    if (variantId) {
      formData.append("variantId", variantId);
    }

    if (deliveryDays) {
      formData.append("deliveryDays", deliveryDays.toString());
    }

    if (designId) {
      formData.append("designId", designId);
    }

    // Disable add to cart button during request
    const addToCartBtn = document.getElementById("btnAddToCart");
    if (addToCartBtn) {
      addToCartBtn.disabled = true;
      addToCartBtn.style.opacity = "0.6";
      addToCartBtn.style.cursor = "not-allowed";
    }

    // Make API request
    const response = await fetch(`${API_CONFIG.baseUrl}/api/create-checkout`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: "不明なエラー" }));
      throw createHttpError(
        errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        response.status
      );
    }

    const result = await response.json();

    if (result.success) {
      // 金額は create-checkout がサーバーで計算してカートの明細プロパティに載せる値（税込）を正とする
      const checkoutPrices = readPricesFromCheckoutUrl(result.checkoutUrl);
      const stashedUnitPrice = resolveUnitPriceExcludingTax(
        checkoutPrices.unitPriceIncludingTax,
        unitPriceExcludingTax,
        variantId
      );
      // 明細の金額（税込）。URL に無いときは税抜単価から order-success と同じ式で概算する
      const lineValue =
        checkoutPrices.lineTotalIncludingTax ??
        (stashedUnitPrice != null
          ? Math.round(stashedUnitPrice * quantity * 1.1)
          : null);

      window.posthog?.capture?.(
        "add_to_cart",
        {
          product_id: productHandle,
          product_name: productName,
          quantity: quantity,
          currency: "JPY",
          value: lineValue,
          unit_price_excluding_tax: stashedUnitPrice,
          manuscript_method: manuscriptMethod,
          source: source,
          variant_id: variantId,
          delivery_days: deliveryDays,
          design_id: designId,
        },
        { send_instantly: true }
      );

      // order_complete（order-success ページ）用に購入明細を退避する。
      // create-checkout の商品もカートに溜まって複数明細になり得るため、既存明細に追加する
      // （決済時に script.cart.js がカートの中身と突き合わせて作り直す）。
      stashPendingOrderItem(
        {
          product_id: productHandle,
          product_name: productName,
          quantity: quantity,
          manuscript_method: manuscriptMethod,
          unit_price_excluding_tax: stashedUnitPrice,
          line_total_including_tax: checkoutPrices.lineTotalIncludingTax,
          variant_id: variantId,
          delivery_days: deliveryDays,
          design_id: designId,
          source: source,
        }
      );

      // Show success message
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        throw new Error("チェックアウトURLが受信されませんでした");
      }
    } else {
      throw new Error(result.error || "カートへの追加に失敗しました");
    }

    return result;
  } catch (error) {
    // Restore button state on error
    restoreAddToCartButton();
    throw error;
  }
}

/**
 * Restore add to cart button to its original state
 */
function restoreAddToCartButton() {
  const addToCartBtn = document.getElementById("btnAddToCart");
  if (addToCartBtn) {
    addToCartBtn.disabled = false;
    addToCartBtn.style.opacity = "1";
    addToCartBtn.style.cursor = "pointer";
  }
}

/**
 * PostHog の order_complete 用に、購入予定の明細を sessionStorage に退避する。
 * Shopify 決済後に到達する order-success ページには注文明細がサーバから渡らないため、
 * カート追加/チェックアウト作成の時点で明細を保存し、成功ページで読み出して発火させる。
 * sessionStorage はタブ単位で保持され、別オリジンのチェックアウトを経由して
 * canbadge のオリジンへ戻っても残る。
 * @param {Object} item - 1 明細分の情報（product_id, product_name, quantity など）
 * @param {boolean} [replace=false] - true なら既存を破棄して単一明細で上書きする
 */
function stashPendingOrderItem(item, replace = false) {
  const STORAGE_KEY = "posthog_pending_order";
  try {
    const stored = replace
      ? null
      : JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
    // 決済へ進んだ印（checkout_started_at）は引き継がず、明細だけを残す。
    // 印を付けた後にカートへ追加したなら、そのタブは決済画面から店へ戻っている（決済をやめた）ので、
    // 注文完了ページが決済を終えた証拠と取り違えないようにする
    const storedItems =
      stored && Array.isArray(stored.items) ? stored.items : [];
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ items: [...storedItems, item] })
    );
  } catch (error) {
    // プライベートモード等で sessionStorage が使えない場合は計測を諦める
  }
}

/**
 * create-checkout が返す checkoutUrl（/cart/add?...&properties[Price per Unit]=...）から、
 * サーバーが計算した単価と明細の金額を読む。どちらもカートに表示される税込の金額。
 * 読めない値は null にする（通信はしない）。
 * @param {string} checkoutUrl - create-checkout の checkoutUrl
 * @returns {{unitPriceIncludingTax: number|null, lineTotalIncludingTax: number|null}}
 */
function readPricesFromCheckoutUrl(checkoutUrl) {
  const toPrice = (value) => {
    const price = parseFloat(value);
    return Number.isFinite(price) && price >= 0 ? price : null;
  };

  try {
    const params = new URL(checkoutUrl, window.location.origin).searchParams;
    return {
      unitPriceIncludingTax: toPrice(params.get("properties[Price per Unit]")),
      lineTotalIncludingTax: toPrice(params.get("properties[Final Price]")),
    };
  } catch (error) {
    return { unitPriceIncludingTax: null, lineTotalIncludingTax: null };
  }
}

/**
 * order_complete 用の税抜単価を決める。
 * 1. create-checkout の単価（税込）を ÷1.1 した概算（overview-simulator と同じ換算）
 * 2. 呼び出し元から渡された税抜単価
 * 3. 商品ページの PRODUCT_DATA のバリエーション価格（税込）を ÷1.1 した概算。
 *    数量・納期の割引が入る前の価格なので、1・2 が取れないときだけ使う
 * @param {number|null} unitPriceIncludingTax - create-checkout の単価（税込）
 * @param {number|null} unitPriceExcludingTax - 呼び出し元から渡された税抜単価
 * @param {string|null} variantId - "gid://shopify/ProductVariant/123" または数字の ID
 * @returns {number|null}
 */
function resolveUnitPriceExcludingTax(
  unitPriceIncludingTax,
  unitPriceExcludingTax,
  variantId
) {
  if (typeof unitPriceIncludingTax === "number") {
    return Math.round(unitPriceIncludingTax / 1.1);
  }
  if (typeof unitPriceExcludingTax === "number") {
    return unitPriceExcludingTax;
  }
  if (!window.PRODUCT_DATA?.variants || variantId == null) {
    return null;
  }

  // 商品ページは variantId を gid の形で渡し、PRODUCT_DATA の id は数字なので、末尾の数字で比べる
  const variantNumber = String(variantId).split("/").pop();
  const matchedVariant = window.PRODUCT_DATA.variants.find(
    (v) => String(v.id) === variantNumber
  );
  return matchedVariant && typeof matchedVariant.price === "number"
    ? Math.round(matchedVariant.price / 1.1)
    : null;
}

/**
 * Get product handle from current page context
 * @returns {string|null} Product handle
 */
function getProductHandleFromContext() {
  // Try to get from window.PRODUCT_DATA first
  if (window.PRODUCT_DATA && window.PRODUCT_DATA.handle) {
    return window.PRODUCT_DATA.handle;
  }

  // Try to get from meta tag
  const metaProductHandle = document.querySelector(
    'meta[name="product-handle"]'
  );
  if (metaProductHandle) {
    return metaProductHandle.getAttribute("content");
  }

  // Try to get from URL path (most reliable method)
  const productHandle = getProductHandle();
  if (productHandle) {
    return productHandle;
  }

  // Try to get from URL parameters as fallback
  const urlParams = new URLSearchParams(window.location.search);
  const productHandleParam =
    urlParams.get("product_handle") || urlParams.get("handle");
  if (productHandleParam) {
    return productHandleParam;
  }

  return null;
}

/**
 * Get selected variant information
 * @returns {Object|null} Variant data
 */
function getSelectedVariantInfo() {
  // Try to get from ProductDetail if available
  if (
    window.ProductDetail &&
    typeof window.ProductDetail.getSelectedVariant === "function"
  ) {
    return window.ProductDetail.getSelectedVariant();
  }

  // Fallback: get from packaging options
  const selectedOption = document.querySelector(".packaging-option.active");
  if (selectedOption) {
    return {
      id: selectedOption.dataset.variantId,
      handle: selectedOption.dataset.variantHandle,
      price: selectedOption.dataset.price,
      type: selectedOption.dataset.packagingType,
      deliveryDays: selectedOption.dataset.deliveryDays,
    };
  }

  return null;
}

/**
 * Get selected quantity and delivery days from modal
 * @returns {Object} Selection data
 */
function getModalSelection() {
  const modal = document.getElementById("designModal");
  if (modal) {
    return {
      quantity: modal.dataset.selectedQuantity,
      deliveryDay: modal.dataset.selectedDeliveryDay,
    };
  }
  return null;
}

/**
 * Illustrator 入稿のエラーを、カートボタンの下に問い合わせリンク付きで出す。
 * トーストは文字だけでリンクを置けないため、モーダル内の #cartErrorMessage を使う。
 * @param {string} message - お客さま向けの文言
 */
function showAddToCartError(message) {
  const errorMsg = document.getElementById("cartErrorMessage");
  if (!errorMsg) {
    showToast(message, "error");
    return;
  }

  const contactLink = document.createElement("a");
  contactLink.href = CONTACT_PAGE_PATH;
  contactLink.className = "text-link-underline";
  contactLink.textContent = "お問い合わせはこちら";

  errorMsg.replaceChildren(
    document.createTextNode(message),
    document.createElement("br"),
    contactLink
  );
  errorMsg.style.display = "block";
}

// アップロードからカート追加までの処理中かどうか（二重送信の防止）
let isAddingToCart = false;

/**
 * Enhanced add to cart function that uses the create-checkout API
 * This function will be called when the Add to Cart button is clicked
 */
async function handleAddToCart() {
  if (isAddingToCart) {
    return;
  }
  isAddingToCart = true;

  // 押した直後に無効化し、処理中であることを文言で伝える。成功時はそのまま遷移するので戻さない
  const addToCartBtn = document.getElementById("btnAddToCart");
  if (addToCartBtn) {
    addToCartBtn.dataset.originalText = addToCartBtn.textContent;
    addToCartBtn.disabled = true;
    addToCartBtn.style.opacity = "0.6";
    addToCartBtn.style.cursor = "not-allowed";
    addToCartBtn.textContent = "送信中…";
  }

  let failedStage = "upload";
  let isRedirecting = false;
  let isCancelled = false;

  try {
    window.posthog?.capture?.("cta_clicked", {
      cta_type: "cart_new",
      source: "product_page",
      manuscript_method: "illustrator",
    });

    // Get product handle
    const productHandle = getProductHandleFromContext();
    if (!productHandle) {
      showToast(
        "商品ハンドルが見つかりません。ページを更新してもう一度お試しください。",
        "error"
      );
      return;
    }

    // Get modal selection (quantity and delivery days)
    const modalSelection = getModalSelection();
    if (!modalSelection || !modalSelection.quantity) {
      showToast("まず数量と配送オプションを選択してください。", "error");
      return;
    }

    // Get selected variant
    const variantInfo = getSelectedVariantInfo();
    if (!variantInfo) {
      showToast("まず梱包オプションを選択してください。", "error");
      return;
    }

    // Check if there's a file to upload
    const fileInput = document.getElementById("fileUpload");

    if (fileInput && fileInput.files && fileInput.files.length > 0) {
      // Hide error message on new attempt
      const errorMsgSuccess = document.getElementById("cartErrorMessage");
      if (errorMsgSuccess) {
        errorMsgSuccess.style.display = "none";
      }

      const uploadedFileResponse = await uploadFileToServer(
        fileInput.files[0],
        productHandle
      );
      const quantity = parseInt(modalSelection.quantity);
      const variantId = `gid://shopify/ProductVariant/${variantInfo.id}`;
      const deliveryDays = parseInt(modalSelection.deliveryDay) || 0;

      // Call the checkout API with designId from upload response
      failedStage = "checkout";
      await addToCartWithCheckout(
        productHandle,
        quantity,
        variantId,
        deliveryDays,
        uploadedFileResponse.data.designId,
        window.PRODUCT_DATA?.title,
        "illustrator"
      );
      isRedirecting = true;
    } else {
      showToast("ファイルがアップロードされていません", "error");
      return;
    }
  } catch (error) {
    // Don't show error message if upload was cancelled by user
    if (error.message === "Upload cancelled") {
      isCancelled = true;
      return;
    }

    // ファイルの中身や個人情報は送らず、失敗した段階と HTTP ステータスだけを記録する
    window.posthog?.capture?.("upload_failed", {
      stage: failedStage,
      status: typeof error.status === "number" ? error.status : null,
    });

    const fallbackMessage =
      failedStage === "upload"
        ? "ファイルのアップロードに失敗しました。"
        : "カートへの追加に失敗しました。";
    showAddToCartError(toUserErrorMessage(error, fallbackMessage));
  } finally {
    if (!isRedirecting) {
      isAddingToCart = false;
      restoreAddToCartButtonText();
      // 取り消し（ファイル削除）のときは deleteUploadedFile がボタンを無効にしているので触らない
      if (!isCancelled) {
        restoreAddToCartButton();
      }
    }
  }
}

// 遷移後にブラウザの「戻る」で bfcache から復元されたとき、処理中のままのボタンを元に戻す
window.addEventListener("pageshow", function (event) {
  if (!event.persisted || !isAddingToCart) {
    return;
  }
  isAddingToCart = false;
  restoreAddToCartButtonText();
  restoreAddToCartButton();
});

/**
 * 処理中に「送信中…」へ変えたカートボタンの文言を元に戻す
 */
function restoreAddToCartButtonText() {
  const addToCartBtn = document.getElementById("btnAddToCart");
  if (addToCartBtn && addToCartBtn.dataset.originalText) {
    addToCartBtn.textContent = addToCartBtn.dataset.originalText;
    delete addToCartBtn.dataset.originalText;
  }
}





// Make functions globally available
window.addToCartWithCheckout = addToCartWithCheckout;
window.handleAddToCart = handleAddToCart;
window.getProductHandleFromContext = getProductHandleFromContext;
window.getSelectedVariantInfo = getSelectedVariantInfo;
window.restoreAddToCartButton = restoreAddToCartButton;
window.uploadFileToServer = uploadFileToServer;
window.showUploadProgress = showUploadProgress;
window.updateUploadProgress = updateUploadProgress;
window.cancelFileUpload = cancelFileUpload;
window.formatFileSize = formatFileSize;